import moment from 'moment-timezone';
import { startOfDay, endOfDay, minutesTo_HH_MM, createBufferInMinutes, } from '../../utils/date-helpers.js';
import { isValidUUID } from '../../utils/string-helpers.js';
import { buildOrConditionsForExternalIds } from '../query-helpers.js';
/** default time range used for the time range selector components */
const defaultTimeRange = {
    timeLow: 0,
    timeHigh: 1439,
};
const isFilteringByTimeRange = ({ low, high }) => {
    if (typeof low === 'number' && low > defaultTimeRange.timeLow)
        return true;
    if (typeof high === 'number' && high < defaultTimeRange.timeHigh)
        return true;
    return false;
};
const arrayFilterActive = (array) => {
    return Array.isArray(array) && array.length > 0;
};
const totalParticipantsWanted = (spots) => spots ? Object.values(spots).reduce((acc, curr) => acc + (curr || 0), 0) : 0;
const buildDateClause = ({ dateLow, dateHigh, }) => {
    return {
        _gte: dateLow ? startOfDay(dateLow) : startOfDay(new Date()),
        ...(dateHigh && { _lte: endOfDay(dateHigh) }),
    };
};
const buildTimeRangeClause = ({ timeLow, timeHigh, }) => {
    return {
        ...(timeLow && { _gte: minutesTo_HH_MM(timeLow) }),
        ...(timeHigh && { _lte: minutesTo_HH_MM(timeHigh) }),
    };
};
const buildSportsFilter = (selectedSportNames) => arrayFilterActive(selectedSportNames)
    ? { sportBySport: { name: { _in: selectedSportNames } } }
    : undefined;
const buildLocationFilters = ({ venueIds, neighborhoodIds, }) => {
    const searchingForLocation = arrayFilterActive(venueIds) || arrayFilterActive(neighborhoodIds);
    if (!searchingForLocation)
        return undefined;
    /**
     * Complicated nesting is to ensure that:
     * - If venue is passed in, we only search for venues
     * - If only neighborhood is passed in, we search for venue -> associated neighborhood
     * - If both are passed in, we search for venue -> associated neighborhood
     */
    return {
        venueByVenue: {
            ...(arrayFilterActive(venueIds) && buildOrConditionsForExternalIds(venueIds)),
            ...(arrayFilterActive(neighborhoodIds) && {
                neighborhoodByNeighborhoodId: {
                    ...buildOrConditionsForExternalIds(neighborhoodIds),
                },
            }),
        },
    };
};
const buildDaysOfWeeksFilter = (selectedDaysOfWeek) => arrayFilterActive(selectedDaysOfWeek)
    ? { days_of_weeks: { day_of_week: { _in: selectedDaysOfWeek } } }
    : undefined;
const buildCompetitionLevelsFilter = (selectedSkillLevels) => arrayFilterActive(selectedSkillLevels)
    ? { competition_levels: { competition_level: { _in: selectedSkillLevels } } }
    : undefined;
const buildProgramTagFilter = (programTagIds) => arrayFilterActive(programTagIds)
    ? { programTagByProgramTag: buildOrConditionsForExternalIds(programTagIds) }
    : undefined;
/** builds the volo pass where clauses used against the current `is_premier` | `is_volo_pass_exclusive` */
const buildVoloPassFilters = ({ premierProgramsOnly, voloPassExclusiveOnly, }) => {
    return {
        ...(premierProgramsOnly && { is_premier: { _eq: true } }),
        ...(voloPassExclusiveOnly && { is_volo_pass_exclusive: { _eq: true } }),
    };
};
/**
 * builds the drop in capacity filter using the computed fields from game_drop_in_capacity view
 *
 * total_men_spots = sum of 'm' and 'a' spots
 * total_flexible_spots = sum of all spots ('m', 'f', 'a')
 */
const buildDropInCapacityFilter = (spots) => {
    const participantsWanted = totalParticipantsWanted(spots);
    const { minimumMen, minimumWomen, minimumNonBinary } = spots ?? {};
    // fallback to make sure there is at least 1 spot available
    if (!participantsWanted)
        return { total_available_spots: { _gte: 1 } };
    return {
        _and: [
            // men can only match m or a spots
            ...(minimumMen
                ? [
                    {
                        total_male_eligible_spots: { _gte: minimumMen },
                    },
                ]
                : []),
            // women/non-binary can match all spots
            ...(minimumWomen || minimumNonBinary
                ? [
                    {
                        total_available_spots: { _gte: participantsWanted },
                    },
                ]
                : []),
        ],
    };
};
// =================================================
// Non Daily Programs
// =================================================
/** builds the where clause for non daily programs */
export const buildDiscoverNonDailyInput = (filters, 
/** UUID (preferred) or City Name (left in to support web discover) */
currentCity, defaultProgramTypes = []) => {
    return {
        where: {
            ...(isValidUUID(currentCity ?? '')
                ? { organization: { _eq: currentCity } }
                : { organizationByOrganization: { name: { _eq: currentCity } } }),
            status: { _eq: 'registration_open' },
            private: { _eq: false },
            archived: { _eq: false },
            registrationByRegistration: {
                registration_close_date: { _gte: 'now()' }, // opposite condition as found in close-league-registration trigger
            },
            ...(filters.selectedGenders?.length ? { gender: { _in: filters.selectedGenders } } : {}),
            program_type: {
                _in: arrayFilterActive(filters.selectedProgramTypes)
                    ? filters.selectedProgramTypes
                    : defaultProgramTypes,
            },
            start_date: buildDateClause({ dateHigh: filters.dateHigh, dateLow: filters.dateLow }),
            ...(isFilteringByTimeRange({ low: filters.timeLow, high: filters.timeHigh }) && {
                start_time_estimate: buildTimeRangeClause({
                    timeHigh: filters.timeHigh,
                    timeLow: filters.timeLow,
                }),
            }),
            ...buildVoloPassFilters({
                premierProgramsOnly: filters.premierProgramsOnly,
                voloPassExclusiveOnly: filters.voloPassExclusiveOnly,
            }),
            ...buildSportsFilter(filters.selectedSportNames),
            ...buildLocationFilters({
                venueIds: filters.selectedVenueIds,
                neighborhoodIds: filters.selectedNeighborhoodIds,
            }),
            ...buildDaysOfWeeksFilter(filters.selectedDaysOfWeek),
            ...buildCompetitionLevelsFilter(filters.selectedSkillLevels),
            ...buildProgramTagFilter(filters.programTagIds),
        },
    };
};
// =================================================
// Daily Programs
// =================================================
export const buildDiscoverDailyInput = (filters, 
/** UUID (preferred) or City Name (left in to support web discover) */
currentCity, defaultProgramTypes = []) => {
    const programTypes = arrayFilterActive(filters.selectedProgramTypes)
        ? filters.selectedProgramTypes
        : defaultProgramTypes;
    const baseLeagueFilters = {
        ...(isValidUUID(currentCity ?? '')
            ? { organization: { _eq: currentCity } }
            : { organizationByOrganization: { name: { _eq: currentCity } } }),
        private: { _eq: false },
        archived: { _eq: false },
        ...(filters.selectedGenders?.length ? { gender: { _in: filters.selectedGenders } } : {}),
        ...buildVoloPassFilters({
            premierProgramsOnly: filters.premierProgramsOnly,
            voloPassExclusiveOnly: filters.voloPassExclusiveOnly,
        }),
        ...buildSportsFilter(filters.selectedSportNames),
        ...buildDaysOfWeeksFilter(filters.selectedDaysOfWeek),
        ...buildCompetitionLevelsFilter(filters.selectedSkillLevels),
        ...buildProgramTagFilter(filters.programTagIds),
    };
    const leagueFilters = {
        ...baseLeagueFilters,
        ...buildLocationFilters({
            venueIds: filters.selectedVenueIds,
            neighborhoodIds: filters.selectedNeighborhoodIds,
        }),
        start_date: buildDateClause({ dateHigh: filters.dateHigh, dateLow: filters.dateLow }),
        // - remove the 'DROPIN' as that is not a valid program type to query against db leagues table
        program_type: { _in: programTypes?.filter(type => type !== 'DROPIN') ?? [] },
        status: { _eq: 'registration_open' },
        registrationByRegistration: {
            available_spots: { _gte: totalParticipantsWanted(filters.spots) || 1 },
            registration_close_date: { _gte: 'now()' }, // opposite condition as found in close-league-registration trigger
        },
    };
    const gameFilters = {
        start_time: {
            _gte: filters.dateLow
                ? startOfDay(filters.dateLow)
                : // #396 - display drop ins in discover for up to 15 minutes after the game has started
                    (() => {
                        // Round to nearest minute to prevent constant updates
                        const now = new Date();
                        now.setSeconds(0, 0);
                        return createBufferInMinutes(now, 15).toISOString();
                    })(),
            ...(filters.dateHigh && { _lte: endOfDay(filters.dateHigh) }),
        },
        ...buildLocationFilters({
            venueIds: filters.selectedVenueIds,
            neighborhoodIds: filters.selectedNeighborhoodIds,
        }),
        drop_in_capacity: buildDropInCapacityFilter(filters.spots),
        leagueByLeague: baseLeagueFilters,
    };
    const queryIncludesDropIns = programTypes?.includes('DROPIN');
    const queryOnlyIncludesDropIns = queryIncludesDropIns && programTypes?.length === 1;
    return {
        where: {
            ...(isFilteringByTimeRange({ low: filters.timeLow, high: filters.timeHigh }) && {
                event_start_time_str: buildTimeRangeClause({
                    timeHigh: filters.timeHigh,
                    timeLow: filters.timeLow,
                }),
            }),
            // note - adding the `_is_null` filters allows the query planner to better optimize & reduce the number of rows scanned
            // - only searching for drop in games only
            ...(queryOnlyIncludesDropIns
                ? {
                    league_id: { _is_null: true },
                    game_id: { _is_null: false },
                    game: gameFilters,
                }
                : // - searching for both drop ins (games) and regular daily programs
                    queryIncludesDropIns
                        ? {
                            _or: [
                                { league_id: { _is_null: false }, league: leagueFilters },
                                { game_id: { _is_null: false }, game: gameFilters },
                            ],
                        }
                        : // - only searching for regular daily programs - no drop in games
                            {
                                game_id: { _is_null: true },
                                league_id: { _is_null: false },
                                league: leagueFilters,
                            }),
        },
    };
};
export const formatDailyPrograms = (leagues) => {
    const dailyPrograms = leagues.reduce((acc, event) => {
        if (!event._id || !event.event_start_date || !event.event_end_time_str)
            return acc;
        if (event.game_id && event.game && event.game.end_time) {
            const gameEntry = {
                key: event._id,
                _id: event._id,
                type: 'result-daily',
                isDropIn: true,
                gameId: event.game_id,
                startDate: event.event_start_date,
                endDate: event.game.end_time,
                isDaily: true,
            };
            return [...acc, gameEntry];
        }
        // for the sort fn, we want both startDate & endDate
        const start = moment(event.event_start_time_str, 'HH:mm');
        const startDate = moment(event.event_start_date)
            .set({
            hour: start.hours(),
            minute: start.minutes(),
        })
            .toISOString();
        const end = moment(event.event_end_time_str, 'HH:mm');
        const endDate = moment(event.event_start_date)
            .set({
            hour: end.hours(),
            minute: end.minutes(),
        })
            .toISOString();
        if (!event.league_id)
            return acc;
        return [
            ...acc,
            {
                _id: event._id,
                key: event._id,
                type: 'result-daily',
                programId: event.league_id,
                startDate,
                endDate,
                isDaily: true,
            },
        ];
    }, []);
    return dailyPrograms;
};
export function sortBasedOnDateTime(a, b) {
    const startDiff = moment(a.startDate).diff(moment(b.startDate));
    if (startDiff === 0) {
        return moment(a.endDate).diff(moment(b.endDate));
    }
    return startDiff;
}
export function formatDateTitle(startDate) {
    return startDate.calendar(null, {
        sameDay: '[Today]',
        nextDay: '[Tomorrow]',
        nextWeek: 'ddd MMM Do',
        sameElse: 'ddd MMM Do',
    });
}
/**
 * Sorts an array of objects by startDate and endDate, and adds date titles to create a daily discovery list.
 */
export const sortDailyDiscovery = (arr = [], addTitles = true) => {
    try {
        const addedTitles = new Set();
        return arr.sort(sortBasedOnDateTime).flatMap(dailyProgram => {
            const startDate = moment.tz(dailyProgram.startDate, moment.tz.guess());
            const title = formatDateTitle(startDate);
            if (!addedTitles.has(title) && addTitles) {
                addedTitles.add(title);
                return [
                    {
                        type: 'dateTitle',
                        isDateTitle: true,
                        key: startDate.toString(),
                        title,
                    },
                    dailyProgram,
                ];
            }
            return [dailyProgram];
        });
    }
    catch (error) {
        console.warn(`sortDailyDiscovery error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return arr;
    }
};
